/**
 * Permission codes the UI gates affordances on (a subset of the server catalog at
 * `apps/api/src/modules/identity/domain/permission.catalog.ts`). These are NOT a security boundary —
 * the server enforces every check; `can()` only hides controls the user can't act on.
 */
export const Perm = {
  OccurrenceApprove: 'occurrence:approve',
  OccurrenceComplete: 'occurrence:complete',
  OccurrenceSkip: 'occurrence:skip',
  RedemptionRequest: 'redemption:request',
  RewardCreateOwn: 'reward:create:own',
  HoldPlace: 'hold:place',
  LedgerReadOwn: 'ledger:read:own',
  // Per-domain reads (replaced the single broad `household:read`). Each gates the read surface of one
  // bounded context; `chore:photo:read` is the sensitive tier (a child's proof image), withheld from
  // the Relative preset. These are NOT a security boundary — the server enforces every check.
  ChoreRead: 'chore:read',
  ChorePhotoRead: 'chore:photo:read',
  HabitRead: 'habit:read',
  EconomyRead: 'economy:read',
  RewardPhotoRead: 'reward:photo:read',
  LearningRead: 'learning:read',
  MemberRead: 'member:read',
  QuestRead: 'quest:read',
  CosmeticsRead: 'cosmetics:read',
  PlayRead: 'play:read',
  LibraryRead: 'library:read',
  /** Admin-only: editing the household settings (name/timezone/locale/currency). */
  HouseholdManage: 'household:manage',
  /**
   * Admin-only: the data-rights surface (export / consent / delete household). The DELETE additionally
   * requires the caller to be the household owner — a check the client cannot make (the session has no
   * owner flag), so it's enforced server-side (`NOT_HOUSEHOLD_OWNER` 403, surfaced kindly in the UI).
   */
  PrivacyManage: 'privacy:manage',
  /** Admin-only: editing a member's profile/avatar (guardians only — a child cannot self-edit). */
  MemberManage: 'member:manage',
  /** Admin-only: inviting a relative (restricted co-guardian) into the household. */
  MemberInvite: 'member:invite',
  /** Admin-only: creating/editing roles + assigning roles to members. */
  RoleManage: 'role:manage',
  /** Create a chore template. Guardians (Parent preset) hold this; the Chores surface gates on it. */
  ChoreCreate: 'chore:create',
  /** Update a chore template (pause/resume/edit). */
  ChoreUpdate: 'chore:update',
  /** Retire a chore template: archive, unarchive (restore), or hard-delete a never-used one. */
  ChoreDelete: 'chore:delete',
  /** Admin-only: the household money knobs (allowance/interest/cash mode/default reward). ADR-0122. */
  EconomySettings: 'economy:settings',
  /**
   * Manage the achievement bonuses / "ways to earn" (create/edit/delete the coin-earning rules). Split
   * out of the old admin-only `economy:configure` and made GRANTABLE (ADR-0122) so a co-parent / custom
   * role can shape how kids earn without the allowance/currency knobs (`economy:settings`).
   */
  EconomyBonusManage: 'economy:bonus:manage',
  /** Admin-only: posting a manual balance adjustment (a money mutation). */
  BalanceAdjust: 'balance:adjust',
  /** View any child's ledger (`:any` superset of `:own`); gates the Economy ledger/balances view. */
  LedgerReadAny: 'ledger:read:any',
  /** Manage own sponsored rewards (a Relative holds this for their own rewards). */
  RewardManageOwn: 'reward:manage:own',
  /** Manage any reward in the catalog (Parent/Admin). */
  RewardManageAny: 'reward:manage:any',
  /** Decide (grant/deny) own-sponsored redemptions (Relative). */
  RedemptionGrantOwn: 'redemption:grant:own',
  /** Decide any redemption (Parent/Admin). */
  RedemptionGrantAny: 'redemption:grant:any',
  /** Lift own-placed privilege holds (Relative). */
  HoldLiftOwn: 'hold:lift:own',
  /** Lift any privilege hold (Parent/Admin). */
  HoldLiftAny: 'hold:lift:any',
  /** Create a habit (guardian; gates the admin Habits "New habit" affordance). */
  HabitCreate: 'habit:create',
  /** Edit/archive a habit (guardian; gates the admin Habits edit/archive actions). */
  HabitManage: 'habit:manage',
  /** Hard-delete an unused habit (no check-in history); mirrors `chore:delete`. Guardian (Parent) only. */
  HabitDelete: 'habit:delete',
  /** Check off OWN habits (a child holds this; gates the kid habit check affordance). */
  HabitCheckOwn: 'habit:check:own',
  /**
   * Buy cosmetics with own coins (ADR-0029 coin sink). Self-scoped by construction (a child can only
   * spend their OWN coins — no `:own`/`:any`); the Child preset holds it, guardians do not. Gates the
   * kid Shop/Customize surface (Buy + the equip toggle + the equipped-cosmetics reads).
   */
  CosmeticPurchase: 'cosmetic:purchase',
  /**
   * Edit/archive/complete cooperative family quests (ADR-0029). A guardian-level code (Parent + Relative
   * presets); a child never manages the shared goal but DOES read it via `quest:read`. Gates the
   * admin Quests edit/archive affordances.
   */
  QuestManage: 'quest:manage',
  /**
   * CREATE a new quest (split out of `quest:manage`) — Parent-only, withheld from the Relative preset,
   * so a restricted co-guardian may edit/archive existing quests but not spin up new ones. Gates the
   * "New quest" affordance + the create path of the quest editor.
   */
  QuestCreate: 'quest:create',
  /**
   * Steer a child's learning — set a topic's intent (focus/available/hidden) for a child (ADR-0034 §6).
   * Guardian-level; gates the per-child learning assignment controls. Now part of the catalog +
   * capability partition (ADR-0122) so it's grantable through the role editor.
   */
  LearningAssign: 'learning:assign',
  /**
   * Author household learning content — create/edit/archive content packs (custom topics, ADR-0034 §7).
   * Guardian-level; gates the Learning authoring surface. Now part of the catalog + capability partition
   * (ADR-0122) so it's grantable through the role editor.
   */
  LearningAuthor: 'learning:author',
} as const;

/**
 * The full permission catalog for the Roles permission matrix — a HAND-MAINTAINED MIRROR of the
 * server's `permission.catalog.ts`. The web app is a separate package and cannot import the API's
 * domain, so this list is kept in sync manually. It FAILS SAFE: the server is the source of truth and
 * rejects any code it doesn't know (and any admin-only code on a non-admin role), so a drift here can
 * at worst show/hide a matrix row — it can never grant a capability the API wouldn't honor.
 * `permission-codes.test.ts` asserts this table's internal consistency (admin-only ⊆ catalog, every
 * `Perm` value present, no dupes, groups covered); it does NOT (and cannot) prove parity with the API.
 *
 * `adminOnly` codes are structurally non-grantable to custom/relative roles, so the role editor never
 * shows them (the API enforces the same). The friendly role builder maps the non-admin codes to
 * plain-language capabilities in `role-capabilities.ts`; `role-capabilities.test.ts` asserts those
 * capabilities partition every non-admin code here.
 */
export interface PermissionMeta {
  readonly code: string;
  readonly group: PermissionGroup;
  readonly adminOnly: boolean;
}

export type PermissionGroup = 'admin' | 'task' | 'economy' | 'habit' | 'quest' | 'general';

/** Admin-only tier — never grantable to relative/custom roles (Parent/Admin preset only). */
const ADMIN_ONLY_CODES: ReadonlySet<string> = new Set([
  'member:invite',
  'member:manage',
  'role:manage',
  'household:manage',
  'household:delete',
  'privacy:manage',
  'economy:settings',
  'balance:adjust',
]);

/** Every permission code, in catalog order, with its group + admin-only flag (for the role matrix). */
export const PERMISSION_CATALOG: readonly PermissionMeta[] = [
  // Admin-only
  { code: 'member:invite', group: 'admin', adminOnly: true },
  { code: 'member:manage', group: 'admin', adminOnly: true },
  { code: 'role:manage', group: 'admin', adminOnly: true },
  { code: 'household:manage', group: 'admin', adminOnly: true },
  { code: 'household:delete', group: 'admin', adminOnly: true },
  { code: 'privacy:manage', group: 'admin', adminOnly: true },
  { code: 'economy:settings', group: 'admin', adminOnly: true },
  { code: 'balance:adjust', group: 'admin', adminOnly: true },
  // Task
  { code: 'chore:create', group: 'task', adminOnly: false },
  { code: 'chore:update', group: 'task', adminOnly: false },
  { code: 'chore:delete', group: 'task', adminOnly: false },
  { code: 'chore:assign', group: 'task', adminOnly: false },
  { code: 'occurrence:complete', group: 'task', adminOnly: false },
  { code: 'occurrence:approve', group: 'task', adminOnly: false },
  { code: 'occurrence:skip', group: 'task', adminOnly: false },
  // Economy
  { code: 'economy:bonus:manage', group: 'economy', adminOnly: false },
  { code: 'reward:create:own', group: 'economy', adminOnly: false },
  { code: 'reward:manage:own', group: 'economy', adminOnly: false },
  { code: 'reward:manage:any', group: 'economy', adminOnly: false },
  { code: 'reward:favorite', group: 'economy', adminOnly: false },
  { code: 'redemption:request', group: 'economy', adminOnly: false },
  { code: 'redemption:grant:own', group: 'economy', adminOnly: false },
  { code: 'redemption:grant:any', group: 'economy', adminOnly: false },
  { code: 'redemption:collect', group: 'economy', adminOnly: false },
  { code: 'hold:place', group: 'economy', adminOnly: false },
  { code: 'hold:lift:own', group: 'economy', adminOnly: false },
  { code: 'hold:lift:any', group: 'economy', adminOnly: false },
  { code: 'ledger:read:own', group: 'economy', adminOnly: false },
  { code: 'ledger:read:any', group: 'economy', adminOnly: false },
  { code: 'savings:manage', group: 'economy', adminOnly: false },
  { code: 'cosmetic:purchase', group: 'economy', adminOnly: false },
  // Habit
  { code: 'habit:create', group: 'habit', adminOnly: false },
  { code: 'habit:manage', group: 'habit', adminOnly: false },
  { code: 'habit:delete', group: 'habit', adminOnly: false },
  { code: 'habit:check:own', group: 'habit', adminOnly: false },
  // Quest (ADR-0029)
  { code: 'quest:manage', group: 'quest', adminOnly: false },
  { code: 'quest:create', group: 'quest', adminOnly: false },
  { code: 'quest:read', group: 'quest', adminOnly: false },
  // Per-domain reads (replaced `household:read`)
  { code: 'chore:read', group: 'task', adminOnly: false },
  { code: 'chore:photo:read', group: 'task', adminOnly: false },
  { code: 'habit:read', group: 'habit', adminOnly: false },
  { code: 'economy:read', group: 'economy', adminOnly: false },
  { code: 'reward:photo:read', group: 'economy', adminOnly: false },
  // General
  { code: 'member:read', group: 'general', adminOnly: false },
  { code: 'learning:read', group: 'general', adminOnly: false },
  // Learning authoring/assignment (ADR-0034 §6/§7) — guardian-level writes, grantable per ADR-0122.
  { code: 'learning:assign', group: 'general', adminOnly: false },
  { code: 'learning:author', group: 'general', adminOnly: false },
  { code: 'cosmetics:read', group: 'general', adminOnly: false },
  { code: 'play:read', group: 'general', adminOnly: false },
  { code: 'play:hero:manage', group: 'general', adminOnly: false },
  { code: 'library:read', group: 'general', adminOnly: false },
];

/** Ordered group keys for the permission catalog (consistency-checked in permission-codes.test.ts). */
export const PERMISSION_GROUPS: readonly PermissionGroup[] = [
  'admin',
  'task',
  'economy',
  'habit',
  'quest',
  'general',
];

/** True if `code` is in the admin-only tier (non-grantable to custom/relative roles). */
export function isAdminOnlyPermission(code: string): boolean {
  return ADMIN_ONLY_CODES.has(code);
}
